import React from "react";
import { Sun, Battery, Zap, ThermometerSun, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  title: "PV-Anlagen",
  description: "Ihre Photovoltaikanlage wird nahtlos in das Smart-Home integriert. Energieflüsse werden automatisch optimiert, Verbraucher gezielt aktiviert und Überschüsse sinnvoll genutzt.",
  icon: Sun,
  stat: "Eigenverbrauch"
}, {
  title: "Wärmepumpen",
  description: "Die Wärmepumpe läuft dann, wenn Energie verfügbar ist. Durch die Kopplung mit PV-Erträgen und Wetterdaten werden Laufzeiten optimiert und Stromkosten reduziert.",
  icon: ThermometerSun,
  stat: "Effizienz"
}, {
  title: "Batteriespeicher",
  description: "Überschüssige Solarenergie wird gespeichert und gezielt eingesetzt. Erhöhen Sie Ihre Autarkie und reduzieren Sie Netzbezug und Lastspitzen.",
  icon: Battery,
  stat: "Autarkie"
}, {
  title: "Wallboxen",
  description: "Ihr Elektrofahrzeug lädt bevorzugt mit eigenem PV-Strom. Intelligentes Lastmanagement verhindert Netzüberlastung und maximiert den Eigenverbrauch.",
  icon: Zap,
  stat: "Mobilität"
}];
export default function Energieoptimierung() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="energieoptimierung" ref={ref} className={`dark bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-10">
        <div className="absolute top-10 left-10 w-64 h-64 border border-primary/30 rounded-full" />
        <div className="absolute bottom-10 right-10 w-96 h-96 border border-primary/20 rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-primary/10 rounded-full" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Energieoptimierung
          </h2>
          <p className="text-muted-foreground text-lg">Wir vernetzen PV-Anlage, Wärmepumpe, Speicher und Wallbox zu einem intelligenten Gesamtsystem – für maximale Effizienz und hohen Eigenverbrauch.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {features.map((feature, index) => <Card key={index} data-index={index} className="bg-card/50 border-border/50 backdrop-blur-sm hover:border-primary/50 transition-colors duration-300 group overflow-hidden rounded-lg">
              <CardHeader className="flex flex-row items-start gap-4 space-y-0 pb-2">
                <div className="relative shrink-0">
                  {}
                  <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full group-hover:bg-primary/30 transition-all duration-500" />
                  
                  {}
                  <div className="relative flex items-center justify-center w-16 h-16 rounded-lg border border-primary/20 bg-background/80 backdrop-blur-md group-hover:border-primary/50 transition-colors">
                    <feature.icon className="w-8 h-8 text-primary" strokeWidth={1.5} />
                    
                    {}
                    <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary/40 rounded-tl-sm" />
                    <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-primary/40 rounded-tr-sm" />
                    <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-primary/40 rounded-bl-sm" />
                    <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-primary/40 rounded-br-sm" />
                  </div>
                </div>
                
                <div className="space-y-1">
                  <CardTitle className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                    {feature.title}
                  </CardTitle>
                  <div className="text-xs font-mono text-secondary uppercase tracking-wider">
                    System: {feature.stat}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <CardDescription className="text-base leading-relaxed">
                  {feature.description}
                </CardDescription>
                
                {}
                <div className="mt-6 pt-6 border-t border-border/30 flex items-center justify-between text-sm text-muted-foreground">
                  <span className="font-mono text-xs text-secondary/80">STATUS: AKTIV</span>
                  <div className="flex gap-1">
                    <div className="w-1 h-1 rounded-full bg-primary animate-pulse" />
                    <div className="w-1 h-1 rounded-full bg-primary/50" />
                    <div className="w-1 h-1 rounded-full bg-primary/30" />
                  </div>
                </div>
              </CardContent>
            </Card>)}
        </div>

        <div className="mt-12 text-center">
          <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_0_20px_-5px_var(--color-primary)] rounded-lg">
            <Link to="/ContactPage">
              Beratung anfordern <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}